import Description from '@/_components/template/Description'
import GotoEndpoint from '@/_components/template/GotoEndpoint'
import Heading from '@/_components/template/Heading'
import SectionBlock from '@/_components/template/SectionBlock'
import SectionHeading from '@/_components/template/SectionHeading'
import { caseStudyHref, CATEGORY_PAGES } from '@/content/portfolioContent'
import { usePageSeo } from '@/lib/seo'
import { Link, Navigate, useParams } from 'react-router-dom'

export default function FeatureCategoryPage() {
    const { slug } = useParams()
    const page = slug ? CATEGORY_PAGES[slug] : undefined

    if (!page) {
        return <Navigate to="/" replace />
    }

    usePageSeo({
        title: `${page.eyebrow} | Tuhin Kairi Portfolio`,
        description: page.intro,
        keywords: [page.eyebrow, page.title, 'Tuhin Kairi portfolio', 'engineering case studies', 'project portfolio'],
    })

    return (
        <section className="relative tracking-wider sm:px-6">
            <main className="container mx-auto min-h-[calc(100vh-10rem)] px-4 pt-32 pb-20 sm:px-5 sm:pt-36 lg:px-0 lg:pt-20">
                <SectionBlock className="grid-cols-1 gap-10 border-b border-border py-12 text-center lg:grid-cols-[1.15fr_0.85fr] lg:gap-12 lg:text-left">
                    <div className="space-y-6">
                        <SectionHeading number="01" content={page.eyebrow} className="justify-center pb-2 lg:justify-start" />
                        <Heading className="mx-auto max-w-[20ch] lg:mx-0">{page.title}</Heading>
                        <Description className="mx-auto max-w-3xl leading-7 text-secondary sm:text-lg lg:mx-0 lg:w-full">
                            {page.intro}
                        </Description>
                    </div>
                    <div className="rounded-[2rem] border border-border bg-surface p-6 sm:p-8">
                        <p className="text-xs font-semibold uppercase tracking-[0.35em] text-secondary">What This Page Shows</p>
                        <Description className="mb-0 mt-6 w-full leading-7 text-secondary sm:text-lg">
                            {page.purpose}
                        </Description>
                    </div>
                </SectionBlock>

                <SectionBlock className="grid-cols-1 gap-8 border-t border-border py-12">
                    <div className="text-center lg:text-left">
                        <SectionHeading number="02" content="Project Cards" className="justify-center pb-4 lg:justify-start" />
                        <Heading className="mx-auto lg:mx-0">Project snapshots with direct paths into the full case studies</Heading>
                    </div>

                    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
                        {page.cards.toReversed().map((card) => ( 
                            <article key={card.id} className="rounded-[2rem] border border-border bg-surface p-6 sm:p-8">
                                <div className="gap-4">
                                    <div className='flex items-center justify-between'>
                                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary ">{card.category}</p>
                                    <span className="rounded-full border border-border px-3 py-1 text-[11px] uppercase tracking-[0.18em] text-secondary">
                                        {card.status}
                                    </span>
                                    </div>
                                        <h2 className="mt-3 text-3xl font-heading text-primary sm:text-4xl ">{card.title}</h2>
                                </div>

                                <Description className="mb-0 mt-5 w-full max-w-none leading-7 text-secondary sm:text-lg">
                                    {card.description}
                                </Description>

                                <div className="mt-6 grid gap-4 text-sm text-primary sm:grid-cols-2 sm:text-base">
                                    <div>
                                        <p className="uppercase tracking-[0.2em] text-secondary">Role</p>
                                        <p className="mt-2">{card.role}</p>
                                    </div>
                                    <div>
                                        <p className="uppercase tracking-[0.2em] text-secondary">Duration</p>
                                        <p className="mt-2">{card.duration}</p>
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-secondary">Key Metrics</p>
                                    <ul className="mt-4 space-y-2 text-sm leading-6 text-primary sm:text-base">
                                        {card.keyMetrics.map((metric) => (
                                            <li key={metric} className="border-b border-border/80 pb-2 last:border-b-0 last:pb-0">
                                                {metric}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="mt-6 flex flex-wrap gap-2">
                                    {card.stack.map((item) => (
                                        <span key={item} className="rounded-full border border-border px-3 py-1.5 text-xs uppercase tracking-[0.15em] text-primary sm:text-sm">
                                            {item}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-5 flex flex-wrap gap-2">
                                    {card.tags.map((tag) => (
                                        <span key={tag} className="rounded-full bg-ground px-3 py-1 text-xs uppercase tracking-[0.15em] text-secondary">
                                            {tag}
                                        </span>
                                    ))}
                                </div>

                                <div className="mt-8 flex flex-wrap gap-3 border-t border-border pt-6">
                                    <Link
                                        to={caseStudyHref(card.projectId, card.detailSection)}
                                        className="rounded-full border border-primary px-4 py-2 text-sm uppercase tracking-[0.18em] text-primary transition-colors hover:bg-primary hover:text-ground"
                                    >
                                        Open Case Study
                                    </Link>
                                    {card.siteUrl ? (
                                        <a
                                            href={card.siteUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="rounded-full border border-border px-4 py-2 text-sm uppercase tracking-[0.18em] text-secondary transition-colors hover:border-primary hover:text-primary"
                                        >
                                            View Site
                                        </a>
                                    ) : null}
                                    {card.sourceUrl ? (
                                        <a
                                            href={card.sourceUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="rounded-full border border-border px-4 py-2 text-sm uppercase tracking-[0.18em] text-secondary transition-colors hover:border-primary hover:text-primary"
                                        >
                                            Source
                                        </a>
                                    ) : null}
                                </div>
                            </article>
                        ))}
                    </div>
                </SectionBlock>

                <div className="pt-6 text-center lg:text-left">
                    <GotoEndpoint content="back to home" endpoint="/#feature-work" className="inline-flex items-center sm:text-lg" />
                </div>
            </main>
        </section>
    )
}
